import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    IQueryContextDatasourceColumn,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';

export interface PlayerKpisDailyDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

export class PlayerKpisDailyDataLoaderFactory extends Factory<
    DataLoader,
    PlayerKpisDailyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'PlayerKpisDailyDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: PlayerKpisDailyDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap;

                // filter out REVENUE_TYPE columns and columns not present in datasource
                const datasourceColumns: IQueryContextDatasourceColumn[] =
                    queryContext.datasourceColumns;

                // generate datasourceSqlMap.columns without REVENUE_TYPE
                const columnsCopy = { ...datasourceSqlMap.columns };
                delete columnsCopy.REVENUE_TYPE;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    columnsCopy
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where } =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,

                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            where: [
                                // TODO remove comment
                                'date >= DATE_SUB(CURRENT_DATE(), INTERVAL 30 DAY)',
                            ],
                        }
                    );

                const usd_pln =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH BASE AS
(
  SELECT ${splitSelect}date
    , SUM(dau) AS DAU
    , SUM(new_users) AS new_users
    , ROUND(SUM(revenue_net_${usd_pln}), 2) AS revenue_net_${usd_pln}
    , ROUND(SUM(revenue_gross_${usd_pln}), 2) AS revenue_gross_${usd_pln}
    , ROUND(SUM(revenue_net_${usd_pln})/SUM(dau), 3) AS ARPDAU_net_${usd_pln}
    , ROUND(SUM(revenue_gross_${usd_pln})/SUM(dau), 3) AS ARPDAU_gross_${usd_pln}
    , ROUND(SUM(inapp_revenue_net_${usd_pln}), 2) AS inapp_revenue_net_${usd_pln}
    , ROUND(SUM(inapp_revenue_gross_${usd_pln}), 2) AS inapp_revenue_gross_${usd_pln}
    , ROUND(SUM(inapp_revenue_net_${usd_pln})/SUM(dau), 3) AS inapp_ARPDAU_net_${usd_pln}
    , ROUND(SUM(inapp_revenue_gross_${usd_pln})/SUM(dau), 3) AS inapp_ARPDAU_gross_${usd_pln}
    , ROUND(SUM(ad_revenue_net_${usd_pln}), 2) AS ad_revenue_net_${usd_pln}
    , ROUND(SUM(ad_revenue_gross_${usd_pln}), 2) AS ad_revenue_gross_${usd_pln}
    , ROUND(SUM(ad_revenue_net_${usd_pln})/SUM(dau), 3) AS ad_ARPDAU_net_${usd_pln}
    , ROUND(SUM(ad_revenue_gross_${usd_pln})/SUM(dau), 3) AS ad_ARPDAU_gross_${usd_pln}
    , ROUND(SUM(daily_payers_count)/SUM(dau), 4) AS conversion_rate
  FROM \`${generateTableFullPath(
      resolvedGcpProjectId,
      datasourceSqlMap.dataset,
      datasourceSqlMap.tableId
  )}\`
  ${where}
  
  GROUP BY ${splitGroupBy}date
  ORDER BY ${splitGroupBy}date
)

SELECT * FROM BASE
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
